/*
 * Flow:
 * 1. Check if image exists in IndexedDB with same last_modified
 * 2. If not exist or different last_modified → fetch from API and cache
 * 3. Display from IndexedDB
 */

import { openDB } from 'idb';

const DB_NAME = 'image-cache-db';
const STORE_NAME = 'image-cache';

interface CachedImageData {
  blob: Blob;
  lastModified: string;
  url: string;
}

async function getDB() {
  return openDB(DB_NAME, 3, {
    upgrade(db, oldVersion) {
      // Create image cache store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
      
      // Clean up old metadata store from previous version if it exists
      if (oldVersion < 3 && db.objectStoreNames.contains('cache-metadata')) {
        db.deleteObjectStore('cache-metadata');
      }
    },
  });
}

export async function getCachedImage(url: string): Promise<CachedImageData | undefined> {
  try {
    const db = await getDB();
    return await db.get(STORE_NAME, url);
  } catch (error) {
    console.error('Error getting cached image:', error);
    return undefined;
  }
}

export async function setCachedImage(url: string, blob: Blob, lastModified: string): Promise<void> {
  try {
    const db = await getDB();
    await db.put(STORE_NAME, { blob, lastModified, url }, url);
  } catch (error) {
    console.error('Error setting cached image:', error);
  }
}

export async function deleteCachedImage(url: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete(STORE_NAME, url);
  } catch (error) {
    console.error('Error deleting cached image:', error);
  }
}

export async function getAllCachedImages(): Promise<CachedImageData[]> {
  try {
    const db = await getDB();
    return await db.getAll(STORE_NAME);
  } catch (error) {
    console.error('Error getting all cached images:', error);
    return [];
  }
}

export async function clearCache(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear(STORE_NAME);
    console.log('Cache cleared successfully');
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
}

export async function getCacheStats(): Promise<{ count: number; totalSize: number }> {
  try {
    const images = await getAllCachedImages();
    const totalSize = images.reduce((sum, img) => sum + img.blob.size, 0);
    return { count: images.length, totalSize };
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return { count: 0, totalSize: 0 };
  }
}
